import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import DatePickerButton from './DatePickerButton';
import FBOAutocomplete from './FBOAutocomplete';
import { useParadigm } from '../../contexts/ParadigmContext';
import {
  initializeUrbanrideApi,
  isConfigured,
  getConfigStatus,
  searchLocations,
  searchAirlines,
  validateFlight,
  getRates,
  bookTrip,
  createAirportLocation,
  createAddressLocation,
  createFboLocation,
  createAsDirectedLocation,
  createPassenger,
  TITLES
} from '../../services/urbanrideService';

/**
 * TripChauffeuredServiceBooking - A trip-aware chauffeured service booking component
 *
 * This component provides a full Urbanride booking flow within a trip context.
 * It allows selecting passengers from trip executives or entering custom passenger details,
 * and automatically adds the confirmed booking as a trip component.
 */
const TripChauffeuredServiceBooking = ({
  tripId,
  tripExecutives = [],
  allExecutives = [],
  allContacts = [],
  onSuccess,
  onCancel
}) => {
  const { labels } = useParadigm();

  // API Configuration state
  const [apiConfigured, setApiConfigured] = useState(false);
  const [apiMode, setApiMode] = useState('test');
  const [configLoading, setConfigLoading] = useState(true);

  // Form step state
  const [step, setStep] = useState(1); // 1: Form, 2: Select Vehicle, 3: Confirmation

  // Pickup state
  const [pickupType, setPickupType] = useState('airport'); // 'airport', 'address', or 'fbo'
  const [pickupLocation, setPickupLocation] = useState(null);
  const [pickupSearch, setPickupSearch] = useState('');
  const [pickupSearchResults, setPickupSearchResults] = useState([]);
  const [pickupSearchLoading, setPickupSearchLoading] = useState(false);
  const [showPickupDropdown, setShowPickupDropdown] = useState(false);
  const [pickupAddressDate, setPickupAddressDate] = useState('');
  const [pickupAddressTime, setPickupAddressTime] = useState('');

  // FBO pickup state
  const [pickupFboAirport, setPickupFboAirport] = useState(null);
  const [pickupFboAirportSearch, setPickupFboAirportSearch] = useState('');
  const [pickupFboAirportResults, setPickupFboAirportResults] = useState([]);
  const [showPickupFboAirportDropdown, setShowPickupFboAirportDropdown] = useState(false);
  const [pickupFboAirportLoading, setPickupFboAirportLoading] = useState(false);
  const [pickupFbo, setPickupFbo] = useState(null);
  const [pickupTailNumber, setPickupTailNumber] = useState('');
  const [pickupFboDate, setPickupFboDate] = useState('');
  const [pickupFboTime, setPickupFboTime] = useState('');

  // Airport pickup details
  const [pickupAirline, setPickupAirline] = useState('');
  const [pickupAirlineCode, setPickupAirlineCode] = useState('');
  const [pickupFlightNumber, setPickupFlightNumber] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [pickupOriginAirport, setPickupOriginAirport] = useState('');
  const [pickupOriginAirportCode, setPickupOriginAirportCode] = useState('');
  const [airlineSearchResults, setAirlineSearchResults] = useState([]);
  const [showAirlineDropdown, setShowAirlineDropdown] = useState(false);
  const [airlineSearchLoading, setAirlineSearchLoading] = useState(false);
  const [originAirportSearchResults, setOriginAirportSearchResults] = useState([]);
  const [showOriginAirportDropdown, setShowOriginAirportDropdown] = useState(false);
  const [originAirportSearchLoading, setOriginAirportSearchLoading] = useState(false);

  // Dropoff state
  const [dropoffType, setDropoffType] = useState('address'); // 'airport' or 'address'
  const [dropoffLocation, setDropoffLocation] = useState(null);
  const [dropoffSearch, setDropoffSearch] = useState('');
  const [dropoffSearchResults, setDropoffSearchResults] = useState([]);
  const [dropoffSearchLoading, setDropoffSearchLoading] = useState(false);
  const [showDropoffDropdown, setShowDropoffDropdown] = useState(false);

  // Airport dropoff details
  const [dropoffAirline, setDropoffAirline] = useState('');
  const [dropoffAirlineCode, setDropoffAirlineCode] = useState('');
  const [dropoffFlightNumber, setDropoffFlightNumber] = useState('');
  const [dropoffDate, setDropoffDate] = useState('');
  const [dropoffTime, setDropoffTime] = useState('');
  const [dropoffDestinationAirport, setDropoffDestinationAirport] = useState('');
  const [dropoffDestinationAirportCode, setDropoffDestinationAirportCode] = useState('');
  const [dropoffAirlineSearchResults, setDropoffAirlineSearchResults] = useState([]);
  const [showDropoffAirlineDropdown, setShowDropoffAirlineDropdown] = useState(false);
  const [dropoffAirlineSearchLoading, setDropoffAirlineSearchLoading] = useState(false);
  const [destinationAirportSearchResults, setDestinationAirportSearchResults] = useState([]);
  const [showDestinationAirportDropdown, setShowDestinationAirportDropdown] = useState(false);
  const [destinationAirportSearchLoading, setDestinationAirportSearchLoading] = useState(false);

  // Passenger state - now supports multiple passengers via search
  const [passengers, setPassengers] = useState([]); // Array of selected passengers
  const [executiveSearch, setExecutiveSearch] = useState('');
  const [executiveSearchResults, setExecutiveSearchResults] = useState([]);
  const [showExecutiveDropdown, setShowExecutiveDropdown] = useState(false);
  const [manualPassenger, setManualPassenger] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    vip: false
  });
  const [showManualForm, setShowManualForm] = useState(false);

  // Stops state - additional stops between pickup and dropoff
  const [stops, setStops] = useState([]); // Array of {type, location, search, duration, searchResults, showDropdown, searchLoading}

  // Stop type options
  const STOP_TYPES = [
    { value: 'address', label: 'Address' },
    { value: 'airport', label: 'Airport' },
    { value: 'train_station', label: 'Train Station' },
    { value: 'fbo', label: 'FBO (Private Aviation)' },
    { value: 'as_directed', label: 'As Directed' }
  ];

  // Duration options in 15-minute increments (0 to 4 hours)
  const DURATION_OPTIONS = [
    { value: '', label: 'No wait time' },
    { value: '15', label: '15 minutes' },
    { value: '30', label: '30 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '60', label: '1 hour' },
    { value: '75', label: '1 hour 15 min' },
    { value: '90', label: '1 hour 30 min' },
    { value: '105', label: '1 hour 45 min' },
    { value: '120', label: '2 hours' },
    { value: '135', label: '2 hours 15 min' },
    { value: '150', label: '2 hours 30 min' },
    { value: '165', label: '2 hours 45 min' },
    { value: '180', label: '3 hours' },
    { value: '195', label: '3 hours 15 min' },
    { value: '210', label: '3 hours 30 min' },
    { value: '225', label: '3 hours 45 min' },
    { value: '240', label: '4 hours' }
  ];

  // Time options in 15-minute increments for address pickup (00:00 - 23:45)
  const TIME_OPTIONS = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const hourStr = String(hour).padStart(2, '0');
      const minuteStr = String(minute).padStart(2, '0');
      const value = `${hourStr}:${minuteStr}`;
      // Format label as 12-hour time with AM/PM
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const label = `${hour12}:${minuteStr} ${ampm}`;
      TIME_OPTIONS.push({ value, label });
    }
  }

  // Comments
  const [clientComments, setClientComments] = useState('');

  // Rates and booking state
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [addingToTrip, setAddingToTrip] = useState(false);

  // Error state
  const [error, setError] = useState('');

  // Flight validation state
  const [flightValidated, setFlightValidated] = useState(false);
  const [flightDetails, setFlightDetails] = useState(null);

  // Refs for debouncing
  const pickupSearchTimeout = useRef(null);
  const dropoffSearchTimeout = useRef(null);
  const airlineSearchTimeout = useRef(null);
  const dropoffAirlineSearchTimeout = useRef(null);
  const originAirportSearchTimeout = useRef(null);
  const destinationAirportSearchTimeout = useRef(null);
  const pickupFboAirportSearchTimeout = useRef(null);

  // Refs to track if a selection was just made (to prevent async searches from reopening dropdown)
  const pickupSelectionMade = useRef(false);
  const dropoffSelectionMade = useRef(false);
  const airlineSelectionMade = useRef(false);
  const dropoffAirlineSelectionMade = useRef(false);
  const originAirportSelectionMade = useRef(false);
  const destinationAirportSelectionMade = useRef(false);
  const pickupFboAirportSelectionMade = useRef(false);

  // Initialize API on mount
  useEffect(() => {
    initApi();
  }, []);

  const initApi = async () => {
    setConfigLoading(true);
    const result = await initializeUrbanrideApi();
    setApiConfigured(result.configured);
    setApiMode(result.mode);
    setConfigLoading(false);
  };

  // Search executives by name
  const handleExecutiveSearch = useCallback((query) => {
    setExecutiveSearch(query);

    if (!query || query.length < 1) {
      setExecutiveSearchResults([]);
      setShowExecutiveDropdown(false);
      return;
    }

    // Search in allExecutives (or tripExecutives if allExecutives not provided)
    const searchList = allExecutives.length > 0 ? allExecutives : tripExecutives;
    const queryLower = query.toLowerCase();

    const results = searchList.filter(exec => {
      const fullName = (exec.full_name || `${exec.first_name} ${exec.last_name}`).toLowerCase();
      const email = (exec.email || '').toLowerCase();
      // Don't show executives already added
      const alreadyAdded = passengers.some(p => p.executiveId === exec.id);
      return !alreadyAdded && (fullName.includes(queryLower) || email.includes(queryLower));
    });

    setExecutiveSearchResults(results.slice(0, 10)); // Limit to 10 results
    setShowExecutiveDropdown(results.length > 0);
  }, [allExecutives, tripExecutives, passengers]);

  // Add executive as passenger
  const addExecutiveAsPassenger = (executive) => {
    // Get the appropriate profile ID based on current API mode
    const { mode } = getConfigStatus();
    const urbanridePaxId = mode === 'production'
      ? (executive.urbanride_pax_id_production || executive.urbanride_pax_id || null)
      : (executive.urbanride_pax_id_test || executive.urbanride_pax_id || null);

    const newPassenger = {
      executiveId: executive.id,
      title: executive.title || '',
      firstName: executive.first_name || '',
      lastName: executive.last_name || '',
      email: executive.email || '',
      mobile: executive.mobile || executive.phone || '',
      vip: executive.vip || false,
      isExecutive: true,
      urbanride_pax_id: urbanridePaxId // Include Urbanride profile ID for booking reuse
    };
    setPassengers([...passengers, newPassenger]);
    setExecutiveSearch('');
    setExecutiveSearchResults([]);
    setShowExecutiveDropdown(false);
  };

  // Add manual passenger
  const addManualPassenger = () => {
    if (!manualPassenger.firstName || !manualPassenger.lastName) {
      return; // Don't add incomplete passenger
    }
    setPassengers([...passengers, { ...manualPassenger, isExecutive: false }]);
    setManualPassenger({ title: '', firstName: '', lastName: '', email: '', mobile: '', vip: false });
    setShowManualForm(false);
  };

  // Remove passenger
  const removePassenger = (index) => {
    setPassengers(passengers.filter((_, i) => i !== index));
  };

  // Stop management functions
  const addStop = () => {
    setStops([...stops, {
      type: 'address',
      location: null,
      search: '',
      duration: '',
      searchResults: [],
      showDropdown: false,
      searchLoading: false
    }]);
  };

  const removeStop = (index) => {
    setStops(stops.filter((_, i) => i !== index));
  };

  const updateStopType = (index, type) => {
    const newStops = [...stops];
    newStops[index] = {
      ...newStops[index],
      type,
      location: null,
      search: '',
      searchResults: []
    };
    setStops(newStops);
  };

  const updateStopDuration = (index, duration) => {
    const newStops = [...stops];
    newStops[index] = { ...newStops[index], duration };
    setStops(newStops);
  };

  const handleStopSearch = useCallback(async (index, query) => {
    // Use functional updates throughout to avoid stale closure issues
    // First, get the stop type before any async operations
    let stopType = 'address';

    setStops(currentStops => {
      const newStops = [...currentStops];
      stopType = newStops[index]?.type || 'address';
      newStops[index] = {
        ...newStops[index],
        search: query,
        location: null
      };
      return newStops;
    });

    if (!query || query.length < 2) {
      setStops(currentStops => {
        const newStops = [...currentStops];
        if (newStops[index]) {
          newStops[index] = {
            ...newStops[index],
            searchResults: [],
            showDropdown: false
          };
        }
        return newStops;
      });
      return;
    }

    // Set loading state
    setStops(currentStops => {
      const newStops = [...currentStops];
      if (newStops[index]) {
        newStops[index] = {
          ...newStops[index],
          searchLoading: true
        };
      }
      return newStops;
    });

    try {
      const locationQuery = { input: query };
      if (stopType === 'airport' || stopType === 'fbo') {
        locationQuery.type = 'airport';
      } else if (stopType === 'train_station') {
        locationQuery.type = 'train';
      }

      const result = await searchLocations(locationQuery);

      // Update with results - use functional update to avoid stale closure
      setStops(currentStops => {
        const updatedStops = [...currentStops];
        if (updatedStops[index]) {
          updatedStops[index] = {
            ...updatedStops[index],
            searchResults: result.locations || [],
            showDropdown: (result.locations || []).length > 0,
            searchLoading: false
          };
        }
        return updatedStops;
      });
    } catch (err) {
      console.error('Stop location search failed:', err);
      // Use functional update to avoid stale closure
      setStops(currentStops => {
        const updatedStops = [...currentStops];
        if (updatedStops[index]) {
          updatedStops[index] = {
            ...updatedStops[index],
            searchResults: [],
            searchLoading: false
          };
        }
        return updatedStops;
      });
    }
  }, []);

  const selectStopLocation = (index, location) => {
    const newStops = [...stops];
    newStops[index] = {
      ...newStops[index],
      location,
      search: location.name,
      searchResults: [],
      showDropdown: false
    };
    setStops(newStops);
  };

  const setStopDropdownVisible = (index, visible) => {
    const newStops = [...stops];
    if (newStops[index]) {
      newStops[index] = { ...newStops[index], showDropdown: visible };
      setStops(newStops);
    }
  };

  // Search locations with debounce
  const handlePickupSearch = useCallback(async (query) => {
    setPickupSearch(query);
    setPickupLocation(null);
    pickupSelectionMade.current = false; // User is typing, reset selection flag

    if (pickupSearchTimeout.current) {
      clearTimeout(pickupSearchTimeout.current);
    }

    if (!query || query.length < 2) {
      setPickupSearchResults([]);
      setShowPickupDropdown(false);
      return;
    }

    pickupSearchTimeout.current = setTimeout(async () => {
      try {
        setPickupSearchLoading(true);
        const locationQuery = { input: query };
        if (pickupType === 'airport') {
          locationQuery.type = 'airport';
        }
        const result = await searchLocations(locationQuery);
        // Only show dropdown if no selection was made while search was in flight
        if (!pickupSelectionMade.current) {
          setPickupSearchResults(result.locations || []);
          if (result.locations?.length > 0) {
            setShowPickupDropdown(true);
          }
        }
      } catch (err) {
        console.error('Location search failed:', err);
        if (!pickupSelectionMade.current) {
          setPickupSearchResults([]);
        }
      } finally {
        setPickupSearchLoading(false);
      }
    }, 300);
  }, [pickupType]);

  const handleDropoffSearch = useCallback(async (query) => {
    setDropoffSearch(query);
    setDropoffLocation(null);
    dropoffSelectionMade.current = false; // User is typing, reset selection flag

    if (dropoffSearchTimeout.current) {
      clearTimeout(dropoffSearchTimeout.current);
    }

    if (!query || query.length < 2) {
      setDropoffSearchResults([]);
      setShowDropoffDropdown(false);
      return;
    }

    dropoffSearchTimeout.current = setTimeout(async () => {
      try {
        setDropoffSearchLoading(true);
        const locationQuery = { input: query };
        if (dropoffType === 'airport') {
          locationQuery.type = 'airport';
        }
        const result = await searchLocations(locationQuery);
        // Only show dropdown if no selection was made while search was in flight
        if (!dropoffSelectionMade.current) {
          setDropoffSearchResults(result.locations || []);
          if (result.locations?.length > 0) {
            setShowDropoffDropdown(true);
          }
        }
      } catch (err) {
        console.error('Location search failed:', err);
        if (!dropoffSelectionMade.current) {
          setDropoffSearchResults([]);
        }
      } finally {
        setDropoffSearchLoading(false);
      }
    }, 300);
  }, [dropoffType]);

  // Airline search
  const handleAirlineSearch = useCallback(async (query, isDropoff = false) => {
    const setAirline = isDropoff ? setDropoffAirline : setPickupAirline;
    const setResults = isDropoff ? setDropoffAirlineSearchResults : setAirlineSearchResults;
    const setShow = isDropoff ? setShowDropoffAirlineDropdown : setShowAirlineDropdown;
    const setLoading = isDropoff ? setDropoffAirlineSearchLoading : setAirlineSearchLoading;
    const timeout = isDropoff ? dropoffAirlineSearchTimeout : airlineSearchTimeout;
    const selectionRef = isDropoff ? dropoffAirlineSelectionMade : airlineSelectionMade;

    setAirline(query);
    selectionRef.current = false; // User is typing, reset selection flag

    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    if (!query || query.length < 1) {
      setResults([]);
      setShow(false);
      return;
    }

    timeout.current = setTimeout(async () => {
      try {
        setLoading(true);
        const result = await searchAirlines({ input: query });
        // Only show dropdown if no selection was made while search was in flight
        if (!selectionRef.current) {
          setResults(result.airlines || []);
          if (result.airlines?.length > 0) {
            setShow(true);
          }
        }
      } catch (err) {
        console.error('Airline search failed:', err);
        if (!selectionRef.current) {
          setResults([]);
        }
      } finally {
        setLoading(false);
      }
    }, 200);
  }, []);

  // Origin/Destination airport search
  const handleOriginAirportSearch = useCallback(async (query) => {
    setPickupOriginAirport(query);
    setPickupOriginAirportCode('');
    originAirportSelectionMade.current = false; // User is typing, reset selection flag

    if (originAirportSearchTimeout.current) {
      clearTimeout(originAirportSearchTimeout.current);
    }

    if (!query || query.length < 2) {
      setOriginAirportSearchResults([]);
      setShowOriginAirportDropdown(false);
      return;
    }

    originAirportSearchTimeout.current = setTimeout(async () => {
      try {
        setOriginAirportSearchLoading(true);
        const result = await searchLocations({ input: query, type: 'airport' });
        // Only show dropdown if no selection was made while search was in flight
        if (!originAirportSelectionMade.current) {
          setOriginAirportSearchResults(result.locations || []);
          if (result.locations?.length > 0) {
            setShowOriginAirportDropdown(true);
          }
        }
      } catch (err) {
        console.error('Origin airport search failed:', err);
        if (!originAirportSelectionMade.current) {
          setOriginAirportSearchResults([]);
        }
      } finally {
        setOriginAirportSearchLoading(false);
      }
    }, 300);
  }, []);

  const handleDestinationAirportSearch = useCallback(async (query) => {
    setDropoffDestinationAirport(query);
    setDropoffDestinationAirportCode('');
    destinationAirportSelectionMade.current = false; // User is typing, reset selection flag

    if (destinationAirportSearchTimeout.current) {
      clearTimeout(destinationAirportSearchTimeout.current);
    }

    if (!query || query.length < 2) {
      setDestinationAirportSearchResults([]);
      setShowDestinationAirportDropdown(false);
      return;
    }

    destinationAirportSearchTimeout.current = setTimeout(async () => {
      try {
        setDestinationAirportSearchLoading(true);
        const result = await searchLocations({ input: query, type: 'airport' });
        // Only show dropdown if no selection was made while search was in flight
        if (!destinationAirportSelectionMade.current) {
          setDestinationAirportSearchResults(result.locations || []);
          if (result.locations?.length > 0) {
            setShowDestinationAirportDropdown(true);
          }
        }
      } catch (err) {
        console.error('Destination airport search failed:', err);
        if (!destinationAirportSelectionMade.current) {
          setDestinationAirportSearchResults([]);
        }
      } finally {
        setDestinationAirportSearchLoading(false);
      }
    }, 300);
  }, []);

  // Select handlers
  const selectOriginAirport = (location) => {
    // Mark selection as made to prevent in-flight searches from reopening dropdown
    originAirportSelectionMade.current = true;
    // Cancel any pending search to prevent it from reopening dropdown
    if (originAirportSearchTimeout.current) {
      clearTimeout(originAirportSearchTimeout.current);
      originAirportSearchTimeout.current = null;
    }
    const airportCode = location.id || location.name.match(/\(([A-Z]{3})\)/)?.[1] || '';
    setPickupOriginAirport(location.name);
    setPickupOriginAirportCode(airportCode);
    setOriginAirportSearchResults([]); // Clear results to prevent dropdown reopening on focus
    setShowOriginAirportDropdown(false);
  };

  const selectDestinationAirport = (location) => {
    // Mark selection as made to prevent in-flight searches from reopening dropdown
    destinationAirportSelectionMade.current = true;
    // Cancel any pending search to prevent it from reopening dropdown
    if (destinationAirportSearchTimeout.current) {
      clearTimeout(destinationAirportSearchTimeout.current);
      destinationAirportSearchTimeout.current = null;
    }
    const airportCode = location.id || location.name.match(/\(([A-Z]{3})\)/)?.[1] || '';
    setDropoffDestinationAirport(location.name);
    setDropoffDestinationAirportCode(airportCode);
    setDestinationAirportSearchResults([]); // Clear results to prevent dropdown reopening on focus
    setShowDestinationAirportDropdown(false);
  };

  // FBO pickup airport search
  const handlePickupFboAirportSearch = useCallback(async (query) => {
    setPickupFboAirportSearch(query);
    setPickupFboAirport(null);
    setPickupFbo(null);
    pickupFboAirportSelectionMade.current = false;

    if (pickupFboAirportSearchTimeout.current) {
      clearTimeout(pickupFboAirportSearchTimeout.current);
    }

    if (!query || query.length < 2) {
      setPickupFboAirportResults([]);
      setShowPickupFboAirportDropdown(false);
      return;
    }

    pickupFboAirportSearchTimeout.current = setTimeout(async () => {
      try {
        setPickupFboAirportLoading(true);
        const result = await searchLocations({ input: query, type: 'airport' });
        if (!pickupFboAirportSelectionMade.current) {
          setPickupFboAirportResults(result.locations || []);
          if (result.locations?.length > 0) {
            setShowPickupFboAirportDropdown(true);
          }
        }
      } catch (err) {
        console.error('FBO airport search failed:', err);
        if (!pickupFboAirportSelectionMade.current) {
          setPickupFboAirportResults([]);
        }
      } finally {
        setPickupFboAirportLoading(false);
      }
    }, 300);
  }, []);

  const selectPickupFboAirport = (location) => {
    pickupFboAirportSelectionMade.current = true;
    if (pickupFboAirportSearchTimeout.current) {
      clearTimeout(pickupFboAirportSearchTimeout.current);
      pickupFboAirportSearchTimeout.current = null;
    }
    // Extract airport code from location (use 3-letter IATA code for Urbanride API)
    const airportCode = location.icao || location.id || location.name.match(/\(([A-Z]{3,4})\)/)?.[1] || '';

    setPickupFboAirport({ ...location, icao: airportCode });
    setPickupFboAirportSearch(location.name);
    setPickupFboAirportResults([]);
    setShowPickupFboAirportDropdown(false);
    setPickupFbo(null); // Reset FBO selection when airport changes
  };

  const handlePickupFboSelect = (fbo) => {
    setPickupFbo(fbo);
  };

  const selectPickupLocation = (location) => {
    // Mark selection as made to prevent in-flight searches from reopening dropdown
    pickupSelectionMade.current = true;
    // Cancel any pending search to prevent it from reopening dropdown
    if (pickupSearchTimeout.current) {
      clearTimeout(pickupSearchTimeout.current);
      pickupSearchTimeout.current = null;
    }
    setPickupLocation(location);
    setPickupSearch(location.name);
    setPickupSearchResults([]); // Clear results to prevent dropdown reopening on focus
    setShowPickupDropdown(false);
  };

  const selectDropoffLocation = (location) => {
    // Mark selection as made to prevent in-flight searches from reopening dropdown
    dropoffSelectionMade.current = true;
    // Cancel any pending search to prevent it from reopening dropdown
    if (dropoffSearchTimeout.current) {
      clearTimeout(dropoffSearchTimeout.current);
      dropoffSearchTimeout.current = null;
    }
    setDropoffLocation(location);
    setDropoffSearch(location.name);
    setDropoffSearchResults([]); // Clear results to prevent dropdown reopening on focus
    setShowDropoffDropdown(false);
  };

  const selectAirline = (airline, isDropoff = false) => {
    if (isDropoff) {
      // Mark selection as made to prevent in-flight searches from reopening dropdown
      dropoffAirlineSelectionMade.current = true;
      // Cancel any pending search to prevent it from reopening dropdown
      if (dropoffAirlineSearchTimeout.current) {
        clearTimeout(dropoffAirlineSearchTimeout.current);
        dropoffAirlineSearchTimeout.current = null;
      }
      setDropoffAirline(airline.name);
      setDropoffAirlineCode(airline.code);
      setDropoffAirlineSearchResults([]); // Clear results to prevent dropdown reopening on focus
      setShowDropoffAirlineDropdown(false);
    } else {
      // Mark selection as made to prevent in-flight searches from reopening dropdown
      airlineSelectionMade.current = true;
      // Cancel any pending search to prevent it from reopening dropdown
      if (airlineSearchTimeout.current) {
        clearTimeout(airlineSearchTimeout.current);
        airlineSearchTimeout.current = null;
      }
      setPickupAirline(airline.name);
      setPickupAirlineCode(airline.code);
      setAirlineSearchResults([]); // Clear results to prevent dropdown reopening on focus
      setShowAirlineDropdown(false);
    }
  };

  // Validate flight
  const handleValidateFlight = async (isDropoff = false) => {
    const airport = isDropoff
      ? (dropoffLocation?.id || dropoffSearch.toUpperCase().substring(0, 3))
      : (pickupLocation?.id || pickupSearch.toUpperCase().substring(0, 3));
    const airline = isDropoff ? dropoffAirlineCode : pickupAirlineCode;
    const flightNumber = isDropoff ? dropoffFlightNumber : pickupFlightNumber;
    const date = isDropoff ? dropoffDate : pickupDate;
    const time = isDropoff ? dropoffTime : pickupTime;

    if (isDropoff) {
      if (!airport || !airline || !flightNumber || !date || !time) {
        setError('Please fill in all flight details before validating');
        return;
      }
    } else {
      if (!airport || !airline || !flightNumber || !date) {
        setError('Please fill in airport, airline, flight number, and date before validating');
        return;
      }
    }

    const dateTime = time ? `${date}T${time}` : `${date}T12:00`;

    try {
      setError('');
      const result = await validateFlight({
        airport: airport.toUpperCase(),
        airline: airline.toUpperCase(),
        flightNumber,
        dateTime,
        departure: isDropoff
      });

      const flightData = result?.flight || result;

      const verifiedTime = isDropoff
        ? (flightData?.departureTimeScheduled || flightData?.departureTime || flightData?.scheduledTime || flightData?.time)
        : (flightData?.arrivalTimeScheduled || flightData?.arrivalTime || flightData?.scheduledTime || flightData?.time);

      if (result && verifiedTime) {
        const scheduledDateTime = new Date(verifiedTime);
        const hours = String(scheduledDateTime.getHours()).padStart(2, '0');
        const minutes = String(scheduledDateTime.getMinutes()).padStart(2, '0');
        const exactTime = `${hours}:${minutes}`;

        if (isDropoff) {
          setDropoffTime(exactTime);
          if (flightData?.destinationAirport && !dropoffDestinationAirportCode) {
            setDropoffDestinationAirportCode(flightData.destinationAirport);
            setDropoffDestinationAirport(flightData.destinationAirport);
          }
        } else {
          setPickupTime(exactTime);
          if (flightData?.departureAirport && !pickupOriginAirportCode) {
            setPickupOriginAirportCode(flightData.departureAirport);
            setPickupOriginAirport(flightData.departureAirport);
          }
        }

        setFlightDetails(result);
        setFlightValidated(true);
        setError(`Flight validated successfully! ${isDropoff ? 'Departure' : 'Arrival'} time updated to ${exactTime}.`);
      } else {
        if (isDropoff) {
          if (flightData?.destinationAirport && !dropoffDestinationAirportCode) {
            setDropoffDestinationAirportCode(flightData.destinationAirport);
            setDropoffDestinationAirport(flightData.destinationAirport);
          }
        } else {
          if (flightData?.departureAirport && !pickupOriginAirportCode) {
            setPickupOriginAirportCode(flightData.departureAirport);
            setPickupOriginAirport(flightData.departureAirport);
          }
        }

        setFlightDetails(result);
        setFlightValidated(true);
        setError('Flight validated successfully!');
      }

      setTimeout(() => {
        if (error.includes('validated successfully')) {
          setError('');
        }
      }, 5000);
    } catch (err) {
      setError(`Flight validation failed: ${err.message}`);
      setFlightValidated(false);
    }
  };

  // Build booking command
  const buildBookingCmd = () => {
    // Determine pickup datetime based on type
    let pickupDateTime;
    if (pickupType === 'airport') {
      pickupDateTime = `${pickupDate}T${pickupTime}`;
    } else if (pickupType === 'fbo') {
      pickupDateTime = `${pickupFboDate}T${pickupFboTime}`;
    } else {
      pickupDateTime = `${pickupAddressDate}T${pickupAddressTime}`;
    }

    // Calculate dropoff datetime
    // Check if there are any interim stops (stops with locations or as_directed type)
    const hasInterimStops = stops.some(stop => stop.type === 'as_directed' || stop.location);

    let dropoffDateTime;
    if (dropoffType === 'airport') {
      dropoffDateTime = `${dropoffDate}T${dropoffTime}`;
    } else if (dropoffType === 'as_directed' && !hasInterimStops) {
      // For "as directed" dropoffs without interim stops, default to 2 hours after pickup
      const pickupDate = new Date(pickupDateTime);
      pickupDate.setHours(pickupDate.getHours() + 2);
      const year = pickupDate.getFullYear();
      const month = String(pickupDate.getMonth() + 1).padStart(2, '0');
      const day = String(pickupDate.getDate()).padStart(2, '0');
      const hours = String(pickupDate.getHours()).padStart(2, '0');
      const minutes = String(pickupDate.getMinutes()).padStart(2, '0');
      dropoffDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
    } else {
      // With interim stops or address dropoff, use pickup time (API will handle estimation)
      dropoffDateTime = pickupDateTime;
    }

    // Build pickup location based on type
    let pickup;
    if (pickupType === 'airport') {
      pickup = createAirportLocation({
        airport: pickupLocation?.id || pickupSearch.toUpperCase().substring(0, 3),
        airline: pickupAirlineCode,
        flightNumber: pickupFlightNumber,
        dateTime: pickupDateTime,
        departure: false
      });
    } else if (pickupType === 'fbo') {
      // FBO uses dedicated FBO location type per API spec
      pickup = createFboLocation({
        id: pickupFbo?.id || '',
        dateTime: pickupDateTime,
        tailNumber: pickupTailNumber || ''
      });
    } else {
      pickup = createAddressLocation({
        id: pickupLocation?.id || '',
        placeId: pickupLocation?.placeId || pickupLocation?.id || '',
        dateTime: pickupDateTime
      });
    }

    const dropoff = dropoffType === 'as_directed'
      ? createAsDirectedLocation({
          dateTime: dropoffDateTime,
          comments: 'Passenger/s will direct to final destination.'
        })
      : dropoffType === 'airport'
        ? createAirportLocation({
            airport: dropoffLocation?.id || dropoffSearch.toUpperCase().substring(0, 3),
            airline: dropoffAirlineCode,
            flightNumber: dropoffFlightNumber,
            dateTime: dropoffDateTime,
            departure: true
          })
        : createAddressLocation({
            id: dropoffLocation?.id || '',
            placeId: dropoffLocation?.placeId || dropoffLocation?.id || '',
            dateTime: dropoffDateTime
          });

    // Build stops array for the API
    const stopsData = stops
      .filter(stop => stop.type === 'as_directed' || stop.location) // Include as_directed stops or stops with selected locations
      .map(stop => {
        // For "as_directed" stops, use the special location type
        if (stop.type === 'as_directed') {
          return createAsDirectedLocation({
            dateTime: pickupDateTime
          });
        }
        // For other stop types, use address location
        return createAddressLocation({
          id: stop.location?.id || '',
          placeId: stop.location?.placeId || stop.location?.id || '',
          dateTime: pickupDateTime, // Stops use the same base datetime
          comments: stop.duration ? `Wait time: ${stop.duration} minutes` : ''
        });
      });

    return {
      pickup,
      dropoff,
      stops: stopsData,
      // Map urbanride_pax_id to id for Urbanride profile reuse
      passengers: passengers.map(p => createPassenger({
        ...p,
        id: p.urbanride_pax_id || undefined
      })),
      clientComments,
      asQuote: false
    };
  };

  // Get rates
  const handleGetRates = async () => {
    setError('');

    // Validate required fields based on pickup type
    if (pickupType === 'fbo') {
      // FBO pickup validation
      if (!pickupFboAirport) {
        setError('Please select an airport for FBO pickup');
        return;
      }
      if (!pickupFbo) {
        setError('Please select an FBO');
        return;
      }
      if (!pickupFboDate || !pickupFboTime) {
        setError('Please select a pickup date and time');
        return;
      }
    } else if (pickupType === 'airport') {
      // Airport pickup validation
      if (!pickupLocation && !pickupSearch) {
        setError('Please select a pickup airport');
        return;
      }
      if (!pickupAirlineCode || !pickupFlightNumber || !pickupDate) {
        setError('Please fill in pickup flight details (airport, airline, flight number, and date)');
        return;
      }
      if (!pickupTime) {
        setError('Please validate the flight to get the arrival time, or enter an arrival time manually');
        return;
      }
    } else {
      // Address pickup validation
      if (!pickupLocation && !pickupSearch) {
        setError('Please select a pickup location');
        return;
      }
      if (!pickupAddressDate || !pickupAddressTime) {
        setError('Please select a pickup date and time');
        return;
      }
    }

    // Dropoff validation
    if (dropoffType !== 'as_directed' && !dropoffLocation && !dropoffSearch) {
      setError('Please select a dropoff location');
      return;
    }
    if (dropoffType === 'airport' && (!dropoffAirlineCode || !dropoffFlightNumber || !dropoffDate || !dropoffTime)) {
      setError('Please fill in all dropoff flight details');
      return;
    }

    if (passengers.length === 0) {
      setError('Please add at least one passenger');
      return;
    }
    // Validate all passengers have required fields
    for (const p of passengers) {
      if (!p.firstName || !p.lastName || !p.email || !p.mobile) {
        setError(`Passenger ${p.firstName || ''} ${p.lastName || ''} is missing required information (email or mobile)`);
        return;
      }
    }

    try {
      setRatesLoading(true);
      const bookingCmd = buildBookingCmd();
      const result = await getRates(bookingCmd);
      setRates(result.rates || []);
      setStep(2);
    } catch (err) {
      console.error('Get Rates Error:', err);
      setError(`Failed to get rates: ${err.message}`);
    } finally {
      setRatesLoading(false);
    }
  };

  // Book trip and add to trip components
  const handleBookTrip = async () => {
    if (!selectedRate) {
      setError('Please select a vehicle');
      return;
    }

    try {
      setError('');
      setBookingLoading(true);
      const bookingCmd = buildBookingCmd();
      bookingCmd.vehicleId = selectedRate.vehicleId;
      bookingCmd.confirmationId = selectedRate.confirmationId;

      const result = await bookTrip(bookingCmd);
      setBookingResult(result);

      // Now add the booking as a trip component
      await addBookingToTrip(result);

      setStep(3);
    } catch (err) {
      setError(`Booking failed: ${err.message}`);
    } finally {
      setBookingLoading(false);
    }
  };

  // Add booking to trip as a component
  const addBookingToTrip = async (booking) => {
    if (!tripId || !booking) return;

    setAddingToTrip(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Determine service type for component data
      let serviceType = 'transfer';
      if (pickupType === 'airport') {
        serviceType = 'airport_pickup';
      } else if (pickupType === 'fbo') {
        serviceType = 'fbo_pickup';
      } else if (dropoffType === 'airport') {
        serviceType = 'airport_dropoff';
      } else if (dropoffType === 'as_directed') {
        serviceType = 'chauffeur_as_directed';
      }

      // Build start datetime based on pickup type
      let startDateTime;
      if (pickupType === 'airport') {
        startDateTime = `${pickupDate}T${pickupTime}`;
      } else if (pickupType === 'fbo') {
        startDateTime = `${pickupFboDate}T${pickupFboTime}`;
      } else {
        startDateTime = `${pickupAddressDate}T${pickupAddressTime}`;
      }

      // Build end datetime
      let endDateTime = null;
      if (dropoffType === 'airport') {
        endDateTime = `${dropoffDate}T${dropoffTime}`;
      }

      // Build component title
      const pickupName = pickupType === 'fbo'
        ? (pickupFbo?.displayName || pickupFbo?.name || pickupFboAirportSearch)
        : (pickupLocation?.name || pickupSearch);
      const dropoffName = dropoffType === 'as_directed' ? 'As Directed' : (dropoffLocation?.name || dropoffSearch);
      const title = `Urbanride: ${pickupName} to ${dropoffName}`;

      // Build location string
      const location = `${pickupName} to ${dropoffName}`;

      // Build passenger names for description
      const passengerNames = passengers.map(p => `${p.firstName} ${p.lastName}`).join(', ');

      const componentData = {
        component_type: 'chauffeur_service',
        title: title,
        description: `${selectedRate?.vehicle || 'Vehicle'} - ${passengerNames}`,
        start_datetime: startDateTime,
        end_datetime: endDateTime || startDateTime,
        location: location,
        status: 'requested',
        confirmation_number: booking.urcn,
        notes: clientComments || '',
        component_data: {
          service_type: serviceType,
          vehicle_class: selectedRate?.vehicle || '',
          vehicle_image_url: selectedRate?.vehicleImageUrl || '',
          pickup_location: pickupName,
          dropoff_location: dropoffName,
          pickup_type: pickupType,
          dropoff_type: dropoffType,
          // Flight details for airport services
          ...(pickupType === 'airport' && {
            pickup_airport_code: pickupLocation?.id || pickupSearch.toUpperCase().substring(0, 3),
            pickup_airline: pickupAirline,
            pickup_airline_code: pickupAirlineCode,
            pickup_flight_number: pickupFlightNumber,
            pickup_flight_date: pickupDate,
            pickup_flight_time: pickupTime,
            pickup_origin_airport: pickupOriginAirportCode || pickupOriginAirport
          }),
          // FBO details for FBO pickup
          ...(pickupType === 'fbo' && {
            pickup_fbo_airport_code: pickupFboAirport?.icao || pickupFboAirport?.id || '',
            pickup_fbo_airport_name: pickupFboAirport?.name || pickupFboAirportSearch || '',
            pickup_fbo_name: pickupFbo?.name || '',
            pickup_fbo_id: pickupFbo?.id || '',
            pickup_fbo_address: pickupFbo?.address || '',
            pickup_tail_number: pickupTailNumber || '',
            pickup_fbo_date: pickupFboDate,
            pickup_fbo_time: pickupFboTime
          }),
          ...(dropoffType === 'airport' && {
            dropoff_airport_code: dropoffLocation?.id || dropoffSearch.toUpperCase().substring(0, 3),
            dropoff_airline: dropoffAirline,
            dropoff_airline_code: dropoffAirlineCode,
            dropoff_flight_number: dropoffFlightNumber,
            dropoff_flight_date: dropoffDate,
            dropoff_flight_time: dropoffTime,
            dropoff_destination_airport: dropoffDestinationAirportCode || dropoffDestinationAirport
          }),
          // Additional stops between pickup and dropoff
          stops: stops.filter(s => s.type === 'as_directed' || s.location).map(s => ({
            type: s.type,
            location: s.type === 'as_directed' ? 'As Directed' : (s.location?.name || s.search),
            location_id: s.type === 'as_directed' ? '' : (s.location?.id || ''),
            duration_minutes: s.type === 'as_directed' ? null : (s.duration ? parseInt(s.duration) : null)
          })),
          // All passengers with full details
          passengers: passengers.map(p => ({
            name: `${p.firstName} ${p.lastName}`,
            email: p.email,
            phone: p.mobile,
            vip: p.vip,
            isExecutive: p.isExecutive,
            executiveId: p.executiveId || null
          })),
          // Legacy single passenger fields (first passenger for backward compatibility)
          passenger_name: passengers.length > 0 ? `${passengers[0].firstName} ${passengers[0].lastName}` : '',
          passenger_email: passengers.length > 0 ? passengers[0].email : '',
          passenger_phone: passengers.length > 0 ? passengers[0].mobile : '',
          passenger_vip: passengers.length > 0 ? passengers[0].vip : false,
          // Rate details
          rate: selectedRate?.rate || '',
          rate_ext: selectedRate?.rateExt || '',
          capacity: selectedRate?.capacity || 0,
          luggage: selectedRate?.luggage || 0,
          cancellation_policy: selectedRate?.cancellationPolicy || '',
          // Booking reference
          urbanride_urcn: booking.urcn,
          urbanride_reservation_id: booking.reservationId || null,
          booking_mode: apiMode
        }
      };

      await axios.post(`${apiBase}/trips/${tripId}/components`, componentData, { headers });
    } catch (err) {
      console.error('Failed to add booking to trip:', err);
      // Don't throw here - the booking succeeded, just the trip component failed
      // User can manually add it later
      setError(`Booking confirmed (${booking.urcn}), but failed to add to trip: ${err.message}`);
    } finally {
      setAddingToTrip(false);
    }
  };

  // Handle success - close and refresh parent
  const handleDone = () => {
    if (onSuccess) {
      onSuccess(bookingResult);
    }
  };

  // Loading state
  if (configLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ui-primary"></div>
      </div>
    );
  }

  // Not configured state
  if (!apiConfigured) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-ui-amber-bg rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-ui-amber-fg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-ui-ink mb-2">Urbanride API Not Configured</h3>
          <p className="text-ui-ink-3 mb-4">
            To book chauffeur services, you need to configure your Urbanride API keys in your profile settings.
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-ui-control rounded-md hover:bg-ui-hover"
            >
              Cancel
            </button>
            <a
              href="/profile"
              className="inline-flex items-center px-4 py-2 bg-ui-primary text-ui-on-primary rounded-md hover:bg-ui-primary-hover"
            >
              Go to Profile Settings
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-h-[80vh] overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 px-1">
        <div>
          <h3 className="text-lg font-semibold text-ui-ink">Book Chauffeured Service</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
              apiMode === 'production' ? 'bg-ui-green-bg text-ui-green-fg' : 'bg-ui-amber-bg text-ui-amber-fg'
            }`}>
              <span className={`w-2 h-2 rounded-full ${apiMode === 'production' ? 'bg-ui-green-strong' : 'bg-ui-amber-strong'}`}></span>
              {apiMode === 'production' ? 'Production' : 'Test Mode'}
            </span>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="mb-6 px-1">
        <div className="flex items-center">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 1 ? 'bg-ui-primary text-ui-on-primary' : 'bg-ui-line text-ui-muted'}`}>1</div>
          <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-ui-primary' : 'bg-ui-line'}`}></div>
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 2 ? 'bg-ui-primary text-ui-on-primary' : 'bg-ui-line text-ui-muted'}`}>2</div>
          <div className={`flex-1 h-1 mx-2 ${step >= 3 ? 'bg-ui-primary' : 'bg-ui-line'}`}></div>
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 3 ? 'bg-ui-primary text-ui-on-primary' : 'bg-ui-line text-ui-muted'}`}>3</div>
        </div>
        <div className="flex justify-between mt-1 text-xs text-ui-muted">
          <span>Trip Details</span>
          <span>Select Vehicle</span>
          <span>Confirmation</span>
        </div>
      </div>

      {error && (
        <div className={`mb-4 p-3 rounded-md ${error.includes('validated successfully') ? 'bg-ui-green-bg border border-ui-green-line text-ui-green-fg' : 'bg-ui-red-bg border border-ui-red-line text-ui-red-fg'}`}>
          {error}
        </div>
      )}

      {/* Step 1: Form */}
      {step === 1 && (
        <div className="space-y-6">
          {/* Pickup Section */}
          <div className="bg-ui-sunk rounded-lg p-4">
            <h4 className="text-md font-semibold text-ui-ink mb-4">Pickup</h4>

            <div className="mb-4">
              <div className="flex items-center gap-4 mb-3">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="pickupType"
                    checked={pickupType === 'airport'}
                    onChange={() => { setPickupType('airport'); setPickupLocation(null); setPickupSearch(''); }}
                  />
                  <span className="text-sm">Airport</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="pickupType"
                    checked={pickupType === 'fbo'}
                    onChange={() => { setPickupType('fbo'); setPickupLocation(null); setPickupSearch(''); setPickupFboAirport(null); setPickupFboAirportSearch(''); setPickupFbo(null); }}
                  />
                  <span className="text-sm">FBO (Private Aviation)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="pickupType"
                    checked={pickupType === 'address'}
                    onChange={() => { setPickupType('address'); setPickupLocation(null); setPickupSearch(''); }}
                  />
                  <span className="text-sm">Address</span>
                </label>
              </div>

              {/* Location Search - for Airport and Address types */}
              {(pickupType === 'airport' || pickupType === 'address') && (
                <div className="relative">
                  <input
                    type="text"
                    value={pickupSearch}
                    onChange={(e) => handlePickupSearch(e.target.value)}
                    onFocus={() => pickupSearchResults.length > 0 && setShowPickupDropdown(true)}
                    placeholder={pickupType === 'airport' ? 'Search airport (e.g., JFK, Los Angeles)' : 'Search address'}
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                  {pickupSearchLoading && (
                    <div className="absolute right-3 top-2.5">
                      <div className="animate-spin h-5 w-5 border-2 border-ui-primary rounded-full border-t-transparent"></div>
                    </div>
                  )}
                  {showPickupDropdown && pickupSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-48 overflow-auto">
                      {pickupSearchResults.map((loc, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectPickupLocation(loc)}
                        >
                          <div className="font-medium text-ui-ink text-sm">{loc.name}</div>
                          <div className="text-xs text-ui-muted">{loc.details}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* FBO Location Search - Two-step: Airport then FBO */}
              {pickupType === 'fbo' && (
                <div className="space-y-3">
                  {/* Step 1: Airport Search */}
                  <div className="relative">
                    <label className="block text-xs font-medium text-ui-ink-2 mb-1">Airport</label>
                    <input
                      type="text"
                      value={pickupFboAirportSearch}
                      onChange={(e) => handlePickupFboAirportSearch(e.target.value)}
                      onFocus={() => pickupFboAirportResults.length > 0 && setShowPickupFboAirportDropdown(true)}
                      placeholder="Search airport (e.g., TEB, Van Nuys)"
                      className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                    />
                    {pickupFboAirportLoading && (
                      <div className="absolute right-3 top-8">
                        <div className="animate-spin h-5 w-5 border-2 border-ui-primary rounded-full border-t-transparent"></div>
                      </div>
                    )}
                    {showPickupFboAirportDropdown && pickupFboAirportResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-48 overflow-auto">
                        {pickupFboAirportResults.map((loc, idx) => (
                          <button
                            key={idx}
                            className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => selectPickupFboAirport(loc)}
                          >
                            <div className="font-medium text-ui-ink text-sm">{loc.name}</div>
                            <div className="text-xs text-ui-muted">{loc.details}</div>
                          </button>
                        ))}
                      </div>
                    )}
                    {pickupFboAirport && (
                      <div className="mt-1 text-xs text-ui-green-fg flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Airport selected: {pickupFboAirport.icao || pickupFboAirport.id}
                      </div>
                    )}
                  </div>

                  {/* Step 2: FBO Selection */}
                  <div>
                    <label className="block text-xs font-medium text-ui-ink-2 mb-1">FBO / Private Terminal</label>
                    <FBOAutocomplete
                      airportIcao={pickupFboAirport?.icao || pickupFboAirport?.id || ''}
                      placeholder="Select FBO..."
                      onFboSelect={handlePickupFboSelect}
                      initialFboName={pickupFbo?.displayName || ''}
                      disabled={!pickupFboAirport}
                      className="text-sm"
                    />
                    {pickupFbo && (
                      <div className="mt-1 text-xs text-ui-green-fg flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        FBO selected
                      </div>
                    )}
                  </div>

                  {/* Tail Number (Optional) */}
                  <div>
                    <label className="block text-xs font-medium text-ui-ink-2 mb-1">Tail Number (Optional)</label>
                    <input
                      type="text"
                      value={pickupTailNumber}
                      onChange={(e) => setPickupTailNumber(e.target.value.toUpperCase())}
                      placeholder="e.g., N123AB"
                      className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Airport-specific fields */}
            {pickupType === 'airport' && (
              <div className="grid md:grid-cols-2 gap-3">
                {/* Airline */}
                <div className="relative">
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Airline</label>
                  <input
                    type="text"
                    value={pickupAirline}
                    onChange={(e) => handleAirlineSearch(e.target.value, false)}
                    onFocus={() => airlineSearchResults.length > 0 && setShowAirlineDropdown(true)}
                    placeholder="Search airline"
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                  {showAirlineDropdown && airlineSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-32 overflow-auto">
                      {airlineSearchResults.map((airline, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0 text-sm"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectAirline(airline, false)}
                        >
                          <span className="font-medium">{airline.code}</span> - {airline.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Flight Number */}
                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Flight Number</label>
                  <input
                    type="text"
                    value={pickupFlightNumber}
                    onChange={(e) => setPickupFlightNumber(e.target.value)}
                    placeholder="e.g., 123"
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                </div>

                {/* Arrival Date */}
                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Arrival Date</label>
                  <DatePickerButton
                    label={pickupDate ? format(new Date(pickupDate), 'EEE, MMM d, yyyy') : 'Select date'}
                    selectedDate={pickupDate ? new Date(pickupDate) : undefined}
                    onPick={(date) => {
                      if (date) {
                        setPickupDate(format(date, 'yyyy-MM-dd'));
                      }
                    }}
                  />
                </div>

                {/* Arrival Time */}
                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Arrival Time</label>
                  <div className="flex gap-2">
                    <input
                      type="time"
                      value={pickupTime}
                      onChange={(e) => setPickupTime(e.target.value)}
                      className="flex-1 px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => handleValidateFlight(false)}
                      disabled={!pickupAirlineCode || !pickupFlightNumber || !pickupDate}
                      className="px-3 py-2 bg-ui-green-strong text-ui-on-primary text-xs rounded-md hover:bg-ui-green-strong/90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      Validate
                    </button>
                  </div>
                </div>

                {/* Origin Airport */}
                <div className="relative md:col-span-2">
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Flight Origin (optional)</label>
                  <input
                    type="text"
                    value={pickupOriginAirport}
                    onChange={(e) => handleOriginAirportSearch(e.target.value)}
                    onFocus={() => originAirportSearchResults.length > 0 && setShowOriginAirportDropdown(true)}
                    placeholder="Search airport"
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                  {showOriginAirportDropdown && originAirportSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-32 overflow-auto">
                      {originAirportSearchResults.map((loc, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0 text-sm"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectOriginAirport(loc)}
                        >
                          {loc.id && <span className="text-ui-link mr-1">{loc.id}</span>} {loc.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Address-specific fields */}
            {pickupType === 'address' && (
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Pickup Date</label>
                  <DatePickerButton
                    label={pickupAddressDate ? format(new Date(pickupAddressDate), 'EEE, MMM d, yyyy') : 'Select date'}
                    selectedDate={pickupAddressDate ? new Date(pickupAddressDate) : undefined}
                    onPick={(date) => {
                      if (date) {
                        setPickupAddressDate(format(date, 'yyyy-MM-dd'));
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Pickup Time</label>
                  <select
                    value={pickupAddressTime}
                    onChange={(e) => setPickupAddressTime(e.target.value)}
                    onFocus={(e) => {
                      // Default to 09:00 when opening dropdown if no value set
                      if (!pickupAddressTime) {
                        setPickupAddressTime('09:00');
                      }
                    }}
                    className="w-full h-10 px-3 border border-ui-line rounded-md bg-ui-surface text-sm focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-ui-primary"
                  >
                    <option value="">Select time</option>
                    {TIME_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* FBO-specific date/time fields */}
            {pickupType === 'fbo' && (
              <div className="grid md:grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Pickup Date</label>
                  <DatePickerButton
                    label={pickupFboDate ? format(new Date(pickupFboDate), 'EEE, MMM d, yyyy') : 'Select date'}
                    selectedDate={pickupFboDate ? new Date(pickupFboDate) : undefined}
                    onPick={(date) => {
                      if (date) {
                        setPickupFboDate(format(date, 'yyyy-MM-dd'));
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Pickup Time</label>
                  <select
                    value={pickupFboTime}
                    onChange={(e) => setPickupFboTime(e.target.value)}
                    onFocus={(e) => {
                      // Default to 09:00 when opening dropdown if no value set
                      if (!pickupFboTime) {
                        setPickupFboTime('09:00');
                      }
                    }}
                    className="w-full h-10 px-3 border border-ui-line rounded-md bg-ui-surface text-sm focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-ui-primary"
                  >
                    <option value="">Select time</option>
                    {TIME_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Additional Stops Section */}
          <div className="bg-ui-sunk rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-semibold text-ui-ink">Additional Stops (Optional)</h4>
              <button
                type="button"
                onClick={addStop}
                className="flex items-center gap-1 text-sm text-ui-link hover:text-ui-link"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Stop
              </button>
            </div>

            {stops.length === 0 ? (
              <p className="text-sm text-ui-muted">No additional stops added. Click "Add Stop" to include stops between pickup and dropoff.</p>
            ) : (
              <div className="space-y-4">
                {stops.map((stop, index) => (
                  <div key={index} className="bg-ui-surface border border-ui-line rounded-lg p-3">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-ui-ink-2">Stop {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeStop(index)}
                        className="text-ui-red-fg hover:text-ui-red-fg p-1"
                        title="Remove stop"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                      {/* Stop Type */}
                      <div className={stop.type === 'as_directed' ? 'md:col-span-2' : ''}>
                        <label className="block text-xs font-medium text-ui-ink-2 mb-1">Stop Type</label>
                        <select
                          value={stop.type}
                          onChange={(e) => updateStopType(index, e.target.value)}
                          className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm bg-ui-surface"
                        >
                          {STOP_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Approximate Wait Time - Hidden for As Directed */}
                      {stop.type !== 'as_directed' && (
                        <div>
                          <label className="block text-xs font-medium text-ui-ink-2 mb-1">Approximate Wait Time</label>
                          <select
                            value={stop.duration}
                            onChange={(e) => updateStopDuration(index, e.target.value)}
                            className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm bg-ui-surface"
                          >
                            {DURATION_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* As Directed Info Message */}
                      {stop.type === 'as_directed' && (
                        <div className="md:col-span-2 bg-ui-blue-bg border border-ui-blue-line rounded-md p-3">
                          <div className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-ui-link mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-ui-blue-fg">Chauffeur Service - As Directed</p>
                              <p className="text-xs text-ui-link mt-1">Passenger/s will direct the driver to one or more stops during the journey.</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Location Search - Full Width (Hidden for As Directed) */}
                      {stop.type !== 'as_directed' && (
                        <div className="md:col-span-2 relative">
                          <label className="block text-xs font-medium text-ui-ink-2 mb-1">
                            {stop.type === 'airport' ? 'Airport' :
                             stop.type === 'train_station' ? 'Train Station' :
                             stop.type === 'fbo' ? 'FBO / Private Terminal' : 'Address'}
                          </label>
                          <input
                            type="text"
                            value={stop.search}
                            onChange={(e) => handleStopSearch(index, e.target.value)}
                            onFocus={() => stop.searchResults?.length > 0 && setStopDropdownVisible(index, true)}
                            placeholder={
                              stop.type === 'airport' ? 'Search airport (e.g., JFK, LAX)' :
                              stop.type === 'train_station' ? 'Search train station' :
                              stop.type === 'fbo' ? 'Search FBO or private terminal' :
                              'Search address'
                            }
                            className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                          />
                          {stop.searchLoading && (
                            <div className="absolute right-3 top-8">
                              <div className="animate-spin h-4 w-4 border-2 border-ui-primary rounded-full border-t-transparent"></div>
                            </div>
                          )}
                          {stop.showDropdown && stop.searchResults?.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-40 overflow-auto">
                              {stop.searchResults.map((loc, locIdx) => (
                                <button
                                  key={locIdx}
                                  type="button"
                                  className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => selectStopLocation(index, loc)}
                                >
                                  <div className="font-medium text-ui-ink text-sm">{loc.name}</div>
                                  {loc.details && <div className="text-xs text-ui-muted">{loc.details}</div>}
                                </button>
                              ))}
                            </div>
                          )}
                          {stop.location && (
                            <div className="mt-1 text-xs text-ui-green-fg flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Location selected
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Dropoff Section */}
          <div className="bg-ui-sunk rounded-lg p-4">
            <h4 className="text-md font-semibold text-ui-ink mb-4">Dropoff</h4>

            <div className="mb-4">
              <div className="flex items-center gap-4 mb-3">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="dropoffType"
                    checked={dropoffType === 'airport'}
                    onChange={() => { setDropoffType('airport'); setDropoffLocation(null); setDropoffSearch(''); }}
                  />
                  <span className="text-sm">Airport</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="dropoffType"
                    checked={dropoffType === 'address'}
                    onChange={() => { setDropoffType('address'); setDropoffLocation(null); setDropoffSearch(''); }}
                  />
                  <span className="text-sm">Address</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="dropoffType"
                    checked={dropoffType === 'as_directed'}
                    onChange={() => { setDropoffType('as_directed'); setDropoffLocation(null); setDropoffSearch(''); }}
                  />
                  <span className="text-sm">As Directed</span>
                </label>
              </div>

              {/* As Directed Info Message */}
              {dropoffType === 'as_directed' && (
                <div className="bg-ui-blue-bg border border-ui-blue-line rounded-md p-3">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-ui-link mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-ui-blue-fg">Chauffeur Service - As Directed</p>
                      <p className="text-xs text-ui-link mt-1">Passenger/s will direct the driver to the final destination.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Location Search - Hidden for As Directed */}
              {dropoffType !== 'as_directed' && (
                <div className="relative">
                  <input
                    type="text"
                    value={dropoffSearch}
                    onChange={(e) => handleDropoffSearch(e.target.value)}
                    onFocus={() => dropoffSearchResults.length > 0 && setShowDropoffDropdown(true)}
                    placeholder={dropoffType === 'airport' ? 'Search airport (e.g., JFK, Los Angeles)' : 'Search address'}
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                  {dropoffSearchLoading && (
                    <div className="absolute right-3 top-2.5">
                      <div className="animate-spin h-5 w-5 border-2 border-ui-primary rounded-full border-t-transparent"></div>
                    </div>
                  )}
                  {showDropoffDropdown && dropoffSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-48 overflow-auto">
                      {dropoffSearchResults.map((loc, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectDropoffLocation(loc)}
                        >
                          <div className="font-medium text-ui-ink text-sm">{loc.name}</div>
                          <div className="text-xs text-ui-muted">{loc.details}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Airport-specific fields */}
            {dropoffType === 'airport' && (
              <div className="grid md:grid-cols-2 gap-3">
                {/* Airline */}
                <div className="relative">
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Airline</label>
                  <input
                    type="text"
                    value={dropoffAirline}
                    onChange={(e) => handleAirlineSearch(e.target.value, true)}
                    onFocus={() => dropoffAirlineSearchResults.length > 0 && setShowDropoffAirlineDropdown(true)}
                    placeholder="Search airline"
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                  {showDropoffAirlineDropdown && dropoffAirlineSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-32 overflow-auto">
                      {dropoffAirlineSearchResults.map((airline, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0 text-sm"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectAirline(airline, true)}
                        >
                          <span className="font-medium">{airline.code}</span> - {airline.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Flight Number */}
                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Flight Number</label>
                  <input
                    type="text"
                    value={dropoffFlightNumber}
                    onChange={(e) => setDropoffFlightNumber(e.target.value)}
                    placeholder="e.g., 456"
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                </div>

                {/* Departure Date */}
                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Departure Date</label>
                  <DatePickerButton
                    label={dropoffDate ? format(new Date(dropoffDate), 'EEE, MMM d, yyyy') : 'Select date'}
                    selectedDate={dropoffDate ? new Date(dropoffDate) : undefined}
                    onPick={(date) => {
                      if (date) {
                        setDropoffDate(format(date, 'yyyy-MM-dd'));
                      }
                    }}
                  />
                </div>

                {/* Departure Time */}
                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Departure Time</label>
                  <div className="flex gap-2">
                    <input
                      type="time"
                      value={dropoffTime}
                      onChange={(e) => setDropoffTime(e.target.value)}
                      className="flex-1 px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => handleValidateFlight(true)}
                      disabled={!dropoffAirlineCode || !dropoffFlightNumber || !dropoffDate || !dropoffTime}
                      className="px-3 py-2 bg-ui-green-strong text-ui-on-primary text-xs rounded-md hover:bg-ui-green-strong/90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      Validate
                    </button>
                  </div>
                </div>

                {/* Destination Airport */}
                <div className="relative md:col-span-2">
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Flight Destination (optional)</label>
                  <input
                    type="text"
                    value={dropoffDestinationAirport}
                    onChange={(e) => handleDestinationAirportSearch(e.target.value)}
                    onFocus={() => destinationAirportSearchResults.length > 0 && setShowDestinationAirportDropdown(true)}
                    placeholder="Search airport"
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                  {showDestinationAirportDropdown && destinationAirportSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-32 overflow-auto">
                      {destinationAirportSearchResults.map((loc, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0 text-sm"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectDestinationAirport(loc)}
                        >
                          {loc.id && <span className="text-ui-link mr-1">{loc.id}</span>} {loc.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Passenger Section */}
          <div className="bg-ui-sunk rounded-lg p-4">
            <h4 className="text-md font-semibold text-ui-ink mb-4">Passengers</h4>

            {/* Added Passengers List */}
            {passengers.length > 0 && (
              <div className="mb-4 space-y-2">
                {passengers.map((p, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-ui-surface p-3 rounded-md border border-ui-line">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-ui-blue-bg rounded-full flex items-center justify-center">
                        <span className="text-ui-link text-sm font-semibold">
                          {p.firstName?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-sm text-ui-ink">
                          {p.firstName} {p.lastName}
                          {p.vip && <span className="ml-2 px-1.5 py-0.5 bg-ui-amber-bg text-ui-amber-fg text-xs rounded">VIP</span>}
                          {p.isExecutive && <span className="ml-2 px-1.5 py-0.5 bg-ui-blue-bg text-ui-link text-xs rounded">{labels.executive}</span>}
                        </div>
                        <div className="text-xs text-ui-muted">{p.email} • {p.mobile}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => removePassenger(idx)}
                      className="text-ui-red-fg hover:text-ui-red-fg p-1"
                      title="Remove passenger"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Executive Search */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-ui-ink-2 mb-1">{labels.searchExecutive}</label>
              <div className="relative">
                <input
                  type="text"
                  value={executiveSearch}
                  onChange={(e) => handleExecutiveSearch(e.target.value)}
                  onFocus={() => executiveSearchResults.length > 0 && setShowExecutiveDropdown(true)}
                  placeholder={labels.searchExecutivesByName}
                  className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                />
                {showExecutiveDropdown && executiveSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-48 overflow-auto">
                    {executiveSearchResults.map((exec) => (
                      <button
                        key={exec.id}
                        className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => addExecutiveAsPassenger(exec)}
                      >
                        <div className="font-medium text-ui-ink text-sm">
                          {exec.full_name || `${exec.first_name} ${exec.last_name}`}
                        </div>
                        <div className="text-xs text-ui-muted">
                          {exec.email && <span>{exec.email}</span>}
                          {exec.phone && <span> • {exec.phone}</span>}
                          {exec.mobile && !exec.phone && <span> • {exec.mobile}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Manual Passenger Entry Toggle */}
            {!showManualForm ? (
              <button
                onClick={() => setShowManualForm(true)}
                className="text-sm text-ui-link hover:text-ui-link flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add passenger manually
              </button>
            ) : (
              <div className="border border-ui-line rounded-md p-3 bg-ui-surface">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-ui-ink-2">Add Manual Passenger</span>
                  <button
                    onClick={() => setShowManualForm(false)}
                    className="text-ui-muted hover:text-ui-ink-3"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-ui-ink-2 mb-1">Title</label>
                    <select
                      value={manualPassenger.title}
                      onChange={(e) => setManualPassenger({ ...manualPassenger, title: e.target.value })}
                      className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                    >
                      {TITLES.map((t) => (
                        <option key={t} value={t}>{t || '(none)'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-2 mt-5">
                      <input
                        type="checkbox"
                        checked={manualPassenger.vip}
                        onChange={(e) => setManualPassenger({ ...manualPassenger, vip: e.target.checked })}
                        className="rounded border-ui-control"
                      />
                      <span className="text-sm font-medium text-ui-ink-2">VIP</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ui-ink-2 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={manualPassenger.firstName}
                      onChange={(e) => setManualPassenger({ ...manualPassenger, firstName: e.target.value })}
                      className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ui-ink-2 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={manualPassenger.lastName}
                      onChange={(e) => setManualPassenger({ ...manualPassenger, lastName: e.target.value })}
                      className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ui-ink-2 mb-1">Email</label>
                    <input
                      type="email"
                      value={manualPassenger.email}
                      onChange={(e) => setManualPassenger({ ...manualPassenger, email: e.target.value })}
                      className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ui-ink-2 mb-1">Mobile Phone</label>
                    <input
                      type="tel"
                      value={manualPassenger.mobile}
                      onChange={(e) => setManualPassenger({ ...manualPassenger, mobile: e.target.value })}
                      placeholder="+1 555-123-4567"
                      className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                    />
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={addManualPassenger}
                    disabled={!manualPassenger.firstName || !manualPassenger.lastName}
                    className="px-3 py-1.5 bg-ui-primary text-ui-on-primary text-sm rounded-md hover:bg-ui-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Passenger
                  </button>
                </div>
              </div>
            )}

            {/* Passenger count indicator */}
            {passengers.length === 0 && (
              <div className="mt-3 text-sm text-ui-amber-fg bg-ui-amber-bg p-2 rounded">
                Please add at least one passenger to continue
              </div>
            )}
          </div>

          {/* Comments */}
          <div className="bg-ui-sunk rounded-lg p-4">
            <label className="block text-xs font-medium text-ui-ink-2 mb-1">Special Instructions (optional)</label>
            <textarea
              value={clientComments}
              onChange={(e) => setClientComments(e.target.value)}
              rows={2}
              placeholder="Any special requests for the driver..."
              className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between pt-4 border-t border-ui-line">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-ui-control rounded-md hover:bg-ui-hover text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleGetRates}
              disabled={ratesLoading}
              className="px-6 py-2 bg-ui-primary text-ui-on-primary rounded-md hover:bg-ui-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {ratesLoading ? 'Getting Rates...' : 'Get Rates'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Select Vehicle */}
      {step === 2 && (
        <div>
          <h4 className="text-md font-semibold text-ui-ink mb-4">Select Vehicle</h4>

          {rates.length === 0 ? (
            <div className="text-center py-8 text-ui-muted">
              No vehicles available for this route.
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {rates.map((rate, idx) => (
                <div
                  key={idx}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedRate?.vehicleId === rate.vehicleId
                      ? 'border-ui-primary bg-ui-blue-bg'
                      : 'border-ui-line hover:border-ui-control'
                  }`}
                  onClick={() => setSelectedRate(rate)}
                >
                  <div className="flex items-start gap-3">
                    {rate.vehicleImageUrl && (
                      <img
                        src={rate.vehicleImageUrl}
                        alt={rate.vehicle}
                        className="w-20 h-14 object-contain"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h5 className="font-semibold text-ui-ink text-sm">{rate.vehicle}</h5>
                        <div className="text-lg font-bold text-ui-link">{rate.rate}</div>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-ui-ink-3 mt-1">
                        <span>Capacity: {rate.capacity}</span>
                        <span>Luggage: {rate.luggage}</span>
                      </div>
                      {rate.cancellationPolicy && (
                        <div className="text-xs text-ui-muted mt-1 truncate">
                          {rate.cancellationPolicy}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-between pt-4 mt-4 border-t border-ui-line">
            <button
              onClick={() => setStep(1)}
              className="px-4 py-2 border border-ui-control rounded-md hover:bg-ui-hover text-sm"
            >
              Back
            </button>
            <button
              onClick={handleBookTrip}
              disabled={!selectedRate || bookingLoading}
              className="px-6 py-2 bg-ui-primary text-ui-on-primary rounded-md hover:bg-ui-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {bookingLoading ? 'Booking...' : 'Confirm Booking'}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Confirmation */}
      {step === 3 && bookingResult && (
        <div className="text-center">
          <div className="w-16 h-16 bg-ui-green-bg rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-ui-green-fg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h3 className="text-xl font-semibold text-ui-ink mb-2">Booking Confirmed!</h3>
          <p className="text-ui-ink-3 mb-4">Your chauffeur service has been booked and added to this trip.</p>

          <div className="bg-ui-sunk rounded-lg p-4 inline-block mb-6">
            <div className="text-sm text-ui-muted">Confirmation Number</div>
            <div className="text-2xl font-bold text-ui-ink">{bookingResult.urcn}</div>
          </div>

          {apiMode === 'test' && (
            <div className="mb-6 p-3 bg-ui-amber-bg border border-ui-amber-line text-ui-amber-fg rounded-md text-sm">
              This is a TEST booking and will not result in an actual reservation.
            </div>
          )}

          {addingToTrip && (
            <div className="mb-4 p-3 bg-ui-blue-bg border border-ui-blue-line text-ui-link rounded-md text-sm">
              Adding booking to trip timeline...
            </div>
          )}

          <div className="flex justify-center">
            <button
              onClick={handleDone}
              className="px-6 py-2 bg-ui-primary text-ui-on-primary rounded-md hover:bg-ui-primary-hover"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TripChauffeuredServiceBooking;
